import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

Vue.use(Vuex);

const baseUrl =
  (window.location.hostname == "localhost"
    ? "http://" + window.location.hostname + ":3000"
    : window.location.origin) + "/api";

export default new Vuex.Store({
  state: {
    categories: [],
    listings: [],
    services: [],
    listingsByCategory: [],
    currentCategory: {},
    currentListing: {},
    tagsByCategory: {},
    searchTerms: [],
    searchResults: [],
    aboutPageData: {},
    images: {},
    sponsors: [],
    adminToken: localStorage.getItem("admin_token") || "",
    providerToken: localStorage.getItem("provider_token") || "",
    pendingProviderCount: 0,
    pendingListingCount: 0,
    kioskMode: false,
    userSearches: [],
    deadLinks: [],
    linkClicks: [],
    removedListingClicks: []
  },
  getters: {
    headers(state) {
      return { Authorization: `Bearer ${state.adminToken}` };
    },
    providerHeaders(state) {
      return { Authorization: `Bearer ${state.providerToken}` };
    },
    baseUrl: () => {
      return (
        (window.location.hostname == "localhost"
          ? "http://" + window.location.hostname + ":3000"
          : window.location.origin) + "/api"
      );
    },
    siteUrl: () => {
      return window.location.hostname == "localhost"
        ? "http://" + window.location.hostname + ":3000"
        : window.location.origin;
    }
  },
  mutations: {
    assignCategories: (state, response) => {
      state.categories = response.data.data;
    },
    assignListings: (state, response) => {
      state.listings = response.data.data;
    },
    assignServices: (state, response) => {
      state.services = response.data.data;
    },
    assignListingsByCategory: (state, response) => {
      // we convert the on- vs. off-post information to a tag
      response.data.data.forEach(listing =>
        listing.tags.push(listing.onbase ? "_onbase" : "_offbase")
      );
      state.listingsByCategory = response.data.data;
    },
    assignCurrentCategory: (state, response) => {
      state.currentCategory = response.data.data;
    },
    assignCurrentListing: (state, response) => {
      state.currentListing = response.data.data;
    },
    assignTagsByCategory: (state, response) => {
      state.tagsByCategory = response.data.data;
    },
    assignSearchTerms: (state, response) => {
      state.searchTerms = response.data.data;
    },
    assignSearchResults: (state, response) => {
      state.searchResults = response.data.data;
    },
    assignAboutPageData: (state, response) => {
      state.aboutPageData = response.data.data;
    },
    assignImages: (state, response) => {
      Vue.set(state.images, response.data.data.id, response.data.data.imgsrc);
    },
    assignSponsors: (state, response) => {
      state.sponsors = response.data.data;
    },
    assignAdminToken: (state, response) => {
      state.adminToken = response.data.token;
      localStorage.setItem("admin_token", state.adminToken);
    },
    clearAdminToken: state => {
      state.adminToken = "";
      localStorage.removeItem("admin_token");
    },
    assignProviderToken: (state, token) => {
      state.providerToken = token;
      if (token) {
        localStorage.setItem("provider_token", token);
      } else {
        localStorage.removeItem("provider_token");
      }
    },
    assignKioskMode: (state, mode) => {
      state.kioskMode = mode;
    },
    assignUserSearches: (state, response) => {
      state.userSearches = response.data.data;
    },
    assignDeadLinks: (state, response) => {
      state.deadLinks = response.data.data;
    },
    assignLinkClicks: (state, response) => {
      state.linkClicks = response.data.data;
    },
    assignRemovedListingClicks: (state, response) => {
      state.removedListingClicks = response.data.data;
    }
  },
  actions: {
    /* categories */
    fetchCategories(context) {
      axios
        .get(`${baseUrl}/categories`)
        .then(response => context.commit("assignCategories", response));
    },
    fetchCategoriesForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/categories?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignCategories", response));
    },
    addCategory(context, payload) {
      axios
        .post(`${baseUrl}/categories`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },
    updateCategory(context, payload) {
      axios
        .put(`${baseUrl}/categories/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },
    deleteCategory(context, payload) {
      axios
        .delete(`${baseUrl}/categories/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },
    saveCategoryOrder(context, orderedIds) {
      axios
        .put(
          `${baseUrl}/categories/order`,
          { orderedIds },
          { headers: this.getters.headers }
        )
        .then(() => this.dispatch("fetchCategoriesForceUpdate"));
    },

    /* listings */
    fetchListings(context) {
      axios
        .get(`${baseUrl}/listings`)
        .then(response => context.commit("assignListings", response));
    },
    fetchListingsForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/listings?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignListings", response));
    },
    // Admin management view needs every listing regardless of status (the
    // public /listings endpoint above only returns 'approved' listings).
    fetchAllListingsAdmin(context) {
      return axios
        .get(`${baseUrl}/listings/admin`, {
          headers: context.getters.headers
        })
        .then(response => context.commit("assignListings", response));
    },
    addListing(context, payload) {
      return axios
        .post(`${baseUrl}/listings`, payload, { headers: this.getters.headers })
        .then(() => this.dispatch("fetchAllListingsAdmin"));
    },
    updateListing(context, payload) {
      return axios
        .put(`${baseUrl}/listings/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchAllListingsAdmin"));
    },
    deleteListing(context, payload) {
      axios
        .delete(`${baseUrl}/listings/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchAllListingsAdmin"));
    },

    /* services */
    fetchServices(context) {
      axios
        .get(`${baseUrl}/services`)
        .then(response => context.commit("assignServices", response));
    },
    fetchServicesForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/services?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignServices", response));
    },
    addService(context, payload) {
      axios
        .post(`${baseUrl}/services`, payload, { headers: this.getters.headers })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    updateService(context, payload) {
      axios
        .put(`${baseUrl}/services/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    deleteService(context, payload) {
      axios
        .delete(`${baseUrl}/services/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    addTag(context, payload) {
      axios
        .post(`${baseUrl}/services/tags`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    updateTag(context, payload) {
      axios
        .put(`${baseUrl}/services/tags/${payload.id}`, payload, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },
    deleteTag(context, payload) {
      axios
        .delete(`${baseUrl}/services/tags/${payload.id}`, {
          headers: this.getters.headers
        })
        .then(() => this.dispatch("fetchServicesForceUpdate"));
    },

    /* listingsByCategory */
    fetchListingsByCategory(context, payload) {
      axios
        .get(`${baseUrl}/listings/`, { params: { category: payload.id } })
        .then(response => context.commit("assignListingsByCategory", response));
    },

    /* currentCategory */
    fetchCategory(context, payload) {
      axios
        .get(`${baseUrl}/categories/${payload.id}`)
        .then(response => context.commit("assignCurrentCategory", response));
    },

    /* currentListing */
    fetchListing(context, payload) {
      axios
        .get(`${baseUrl}/listings/${payload.id}`)
        .then(response => context.commit("assignCurrentListing", response));
    },

    /* tagsByCategory **/
    fetchTagsByCategory(context, payload) {
      axios
        .get(`${baseUrl}/services`, { params: { category: payload.id } })
        .then(response => context.commit("assignTagsByCategory", response));
    },

    /* searchTerms */
    fetchSearchTerms(context) {
      axios
        .get(`${baseUrl}/search/terms`)
        .then(response => context.commit("assignSearchTerms", response));
    },

    /* searchResults */
    fetchSearchResults(context, payload) {
      axios
        .get(`${baseUrl}/search`, { params: { term: payload.searchTerm } })
        .then(response => context.commit("assignSearchResults", response));
    },

    /* aboutPageData */
    fetchAboutPageData(context) {
      axios
        .get(`${baseUrl}/about/`)
        .then(response => context.commit("assignAboutPageData", response));
    },

    /* images */
    fetchImage(context, payload) {
      axios
        .get(`${baseUrl}/images/${payload.id}`)
        .then(response => context.commit("assignImages", response));
    },

    /* sponsors */
    fetchSponsors(context) {
      axios
        .get(`${baseUrl}/sponsors`)
        .then(response => context.commit("assignSponsors", response));
    },
    fetchSponsorsForceUpdate(context) {
      // we don't want the browser to use a cached response
      // so we use the trick of adding a timestamp query parameter
      axios
        .get(`${baseUrl}/sponsors?timestamp=${new Date().getTime()}`)
        .then(response => context.commit("assignSponsors", response));
    },
    updateSponsors(context, payload) {
      axios
        .put(`${baseUrl}/sponsors`, payload, { headers: this.getters.headers })
        .then(() => this.dispatch("fetchSponsorsForceUpdate"));
    },
    /* user searches */
    fetchUserSearches(context) {
      axios
        .get(`${baseUrl}/user-searches`)
        .then(response => context.commit("assignUserSearches", response));
    },
    /* dead links */
    fetchDeadLinks(context) {
      return axios
        .get(`${baseUrl}/dead-links`, { headers: this.getters.headers })
        .then(response => context.commit("assignDeadLinks", response));
    },
    checkDeadLinks(context) {
      return axios
        .post(
          `${baseUrl}/dead-links/check`,
          {},
          { headers: this.getters.headers }
        )
        .then(response => context.commit("assignDeadLinks", response));
    },
    fetchDeadLinksProgress() {
      return axios
        .get(`${baseUrl}/dead-links/progress`, {
          headers: this.getters.headers
        })
        .then(response => response.data);
    },
    /* link clicks */
    fetchLinkClicks(context) {
      return axios
        .get(`${baseUrl}/link-clicks`, { headers: this.getters.headers })
        .then(response => context.commit("assignLinkClicks", response));
    },
    deleteRemovedListingClicks(context, listingId) {
      return axios.delete(`${baseUrl}/link-clicks/removed/${listingId}`, {
        headers: this.getters.headers
      });
    },
    fetchRemovedListingClicks(context) {
      return axios
        .get(`${baseUrl}/link-clicks/removed`, {
          headers: this.getters.headers
        })
        .then(response =>
          context.commit("assignRemovedListingClicks", response)
        );
    },
    restoreListing(context, listingId) {
      return axios
        .post(
          `${baseUrl}/listings/${listingId}/restore`,
          {},
          {
            headers: this.getters.headers
          }
        )
        .then(() => this.dispatch("fetchRemovedListingClicks"));
    },
    /* adminToken */
    login(context, payload) {
      axios
        .post(`${baseUrl}/login`, payload)
        .then(response => context.commit("assignAdminToken", response));
    },
    /* providerToken */
    loginProvider(context, payload) {
      return axios
        .post(`${baseUrl}/providers/login`, payload)
        .then(response =>
          context.commit("assignProviderToken", response.data.token)
        )
        .catch(err => {
          const message =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Login failed";
          return Promise.reject(new Error(message));
        });
    },
    registerProvider(context, payload) {
      return axios.post(`${baseUrl}/providers/register`, payload).catch(err => {
        const message =
          err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Registration failed";
        return Promise.reject(new Error(message));
      });
    },
    /* provider listing management */
    fetchProviderListings() {
      return axios
        .get(`${baseUrl}/provider-listings`, {
          headers: this.getters.providerHeaders
        })
        .then(response => response.data.data);
    },
    createProviderListing(context, payload) {
      return axios
        .post(`${baseUrl}/provider-listings`, payload, {
          headers: this.getters.providerHeaders
        })
        .catch(err => {
          const message =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Failed to create listing.";
          return Promise.reject(new Error(message));
        });
    },
    updateProviderListing(context, payload) {
      return axios
        .put(`${baseUrl}/provider-listings/${payload.id}`, payload, {
          headers: this.getters.providerHeaders
        })
        .catch(err => {
          const message =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Failed to submit edit.";
          return Promise.reject(new Error(message));
        });
    },
    /* admin pending listing management */
    fetchPendingListings(context) {
      return axios
        .get(`${baseUrl}/listings/pending`, { headers: this.getters.headers })
        .then(response => {
          context.state.pendingListingCount = response.data.data.length;
          return response.data.data;
        });
    },
    approvePendingListing(context, id) {
      return axios
        .put(
          `${baseUrl}/listings/${id}/approve`,
          {},
          { headers: this.getters.headers }
        )
        .then(response => {
          if (context.state.pendingListingCount > 0) {
            context.state.pendingListingCount--;
          }
          return response;
        });
    },
    denyPendingListing(context, { id, reason }) {
      return axios
        .put(
          `${baseUrl}/listings/${id}/deny`,
          { reason },
          { headers: this.getters.headers }
        )
        .then(response => {
          if (context.state.pendingListingCount > 0) {
            context.state.pendingListingCount--;
          }
          return response;
        });
    },
    /* admin notification settings */
    fetchAdminSettings() {
      return axios
        .get(`${baseUrl}/admin-settings`, { headers: this.getters.headers })
        .then(response => response.data.data);
    },
    updateAdminSettings(context, payload) {
      return axios
        .put(`${baseUrl}/admin-settings`, payload, {
          headers: this.getters.headers
        })
        .catch(err => {
          const message =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Failed to save settings.";
          return Promise.reject(new Error(message));
        });
    },
    /* admin provider management */
    fetchProviders(context) {
      return axios
        .get(`${baseUrl}/providers`, { headers: this.getters.headers })
        .then(response => {
          const providers = response.data.data;
          context.state.pendingProviderCount = providers.filter(
            p => p.status === "pending"
          ).length;
          return providers;
        });
    },
    approveProvider(context, id) {
      return axios
        .put(
          `${baseUrl}/providers/${id}/approve`,
          {},
          { headers: this.getters.headers }
        )
        .then(response => {
          if (context.state.pendingProviderCount > 0) {
            context.state.pendingProviderCount--;
          }
          return response;
        });
    },
    declineProvider(context, id) {
      return axios
        .put(
          `${baseUrl}/providers/${id}/decline`,
          {},
          { headers: this.getters.headers }
        )
        .then(response => {
          if (context.state.pendingProviderCount > 0) {
            context.state.pendingProviderCount--;
          }
          return response;
        });
    },
    fetchProviderListingsAdmin(context, id) {
      return axios
        .get(`${baseUrl}/providers/${id}/listings`, {
          headers: this.getters.headers
        })
        .then(response => response.data.data);
    },
    deleteProvider(context, { id, deleteListings }) {
      return axios.delete(
        `${baseUrl}/providers/${id}?deleteListings=${deleteListings}`,
        { headers: this.getters.headers }
      );
    },
    assignListing(context, { listingId, providerIds }) {
      return axios.put(
        `${baseUrl}/listings/${listingId}/assign`,
        { provider_ids: providerIds || [] },
        { headers: this.getters.headers }
      );
    },
    changePassword(context, payload) {
      return axios
        .put(`${baseUrl}/users`, payload)
        .then(response => context.commit("assignAdminToken", response));
    }
  }
});
